// SheetSwitchAnimation — full-screen overlay som visar en kort
// slide-animation när user byter sheet via båg-svep.
//
// Trigger: parent håller en `trigger`-key som ändras (ny direction-värde)
// → animationen startar. CSS-keyframes hanterar själva rörelsen.
//
// Respekterar `prefers-reduced-motion: reduce` — då visas bara en
// stillbild med riktningspilen i en halv sekund.

import { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface SheetSwitchAnimationProps {
  /** En ny key + direction triggar animation. Sätt till null för att dölja. */
  trigger: { key: number; direction: 'fwd' | 'back'; sheetLabel?: string } | null;
}

const ANIMATION_MS = 300;

export function SheetSwitchAnimation({ trigger }: SheetSwitchAnimationProps) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!trigger) return;
    setVisible(true);
    const t = setTimeout(() => setVisible(false), ANIMATION_MS);
    return () => clearTimeout(t);
  }, [trigger]);

  if (!visible || !trigger) return null;

  const isFwd = trigger.direction === 'fwd';

  return (
    <div
      className="fixed inset-0 z-50 pointer-events-none flex items-center justify-center"
      aria-hidden="true"
    >
      <div
        className="bg-background/90 border border-border rounded-2xl shadow-2xl px-6 py-4 flex items-center gap-3 animate-in fade-in zoom-in-95 duration-200"
        style={{
          animationDuration: `${ANIMATION_MS}ms`,
        }}
      >
        {!isFwd && <ChevronLeft className="w-8 h-8 text-primary" />}
        <div className="text-base font-semibold text-foreground">
          {trigger.sheetLabel ?? (isFwd ? 'Nästa sheet' : 'Föregående sheet')}
        </div>
        {isFwd && <ChevronRight className="w-8 h-8 text-primary" />}
      </div>
    </div>
  );
}
